import { MouseEvent, useState, useEffect } from 'react';
import { ThumbsUp } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useToggleProjectGroupLikeMutation } from './like.api.slice';
import { setShowLoginDialog } from '../global/global.slice';
import { UserState } from '../auth/auth.d';

function ProjectGroupLikeButton({
  liked,
  likes,
  projectGroupId,
  size = 'sm',
  className,
}: {
  liked: boolean | undefined;
  likes: number;
  projectGroupId: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const dispatch = useDispatch();
  const { user } = useSelector((state: { auth: UserState }) => state.auth);
  const [toggleLike] = useToggleProjectGroupLikeMutation();

  // Local state for optimistic updates
  const [isLiked, setIsLiked] = useState(liked ?? false);
  const [likeCount, setLikeCount] = useState(likes);

  // Sync with props when they change (e.g., after refetch)
  useEffect(() => {
    setIsLiked(liked ?? false);
    setLikeCount(likes);
  }, [liked, likes]);

  const handleToggleLike = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    // Check if user is logged in
    if (!user) {
      dispatch(setShowLoginDialog(true));
      return;
    }

    // Optimistic update for local UI state
    const wasLiked = isLiked;
    setIsLiked(!wasLiked);
    setLikeCount(wasLiked ? likeCount - 1 : likeCount + 1);

    try {
      await toggleLike({
        projectGroupId,
        currentlyLiked: wasLiked,
      }).unwrap();
    } catch {
      // Revert on failure
      setIsLiked(wasLiked);
      setLikeCount(wasLiked ? likeCount : likeCount - 1);
    }
  };

  if (isLiked) {
    return (
      <Button
        variant="secondary"
        size={size}
        className={cn(
          'flex items-center gap-4 justify-center border-gray-400 border-[1.5px] text-black text-[13px] bg-white hover:bg-white',
          className
        )}
        onClick={handleToggleLike}
      >
        <ThumbsUp fill="#292929" stroke="#292929" size={16} />
        <p>{likeCount}</p>
      </Button>
    );
  }

  return (
    <Button
      variant="outline"
      size={size}
      onClick={handleToggleLike}
      className={cn(
        'flex items-center gap-4 justify-center text-[13px]',
        className
      )}
    >
      <ThumbsUp stroke="#292929" size={16} />
      <p>{likeCount}</p>
    </Button>
  );
}

export default ProjectGroupLikeButton;
